import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFaceSmile, faPaste } from '@fortawesome/free-regular-svg-icons'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { setReplySent, setShowEmojiModal } from '../../../actions/ui'
import { useDispatch, useSelector } from 'react-redux'
import { editReply, sendReply, setCommentReview } from '../../../actions/requests'
import Button from '../Button'
import './ReplyForm.scss'
import SavedRepliesModal from '../SavedRepliesModal'
import EmojiContainer from '../EmojiContainer'
import { addNewTmpComment, updateComment } from '../../../actions/pages'
import Translate from '../CommentItem/Actions/Translate'
import ReactTooltip from 'react-tooltip'
import AiAction from './AiAction'
import { MentionsInput, Mention } from 'react-mentions'

// minimal styles (adjust to your theme if needed)
const mentionsStyle = {
    width: '100%',
    minHeight: 90,
}
const suggestionsListStyle = {
    zIndex: 9999,
    background: '#fff',
    overflow: 'hidden',
    fontWeight: 700,
    position: 'relative',
    color: '#2bae76'
}
const suggestionItemBase = { padding: '5px 10px', cursor: 'pointer' }

function insertMentionMarkupAtCaret({
                                        markupValue,
                                        plainValue,
                                        textareaEl,
                                        mentionId,
                                        mentionDisplay,
                                        textAfter = '',
                                    }) {
    const safeDisplay = normalizeDisplay(mentionDisplay)
    const mentionMarkup = `@[${safeDisplay}](${mentionId})`
    const mentionPlain = `@${safeDisplay}`

    const caret = textareaEl?.selectionStart ?? plainValue.length

    const beforePlain = plainValue.slice(0, caret)
    const afterPlain = plainValue.slice(caret)

    // convert plain caret position -> markup caret position
    let plainPos = 0
    let markupPos = 0

    while (markupPos < markupValue.length && plainPos < caret) {
        const rest = markupValue.slice(markupPos)
        const match = rest.match(/^@\[(.+?)\]\((.+?)\)/)

        if (match) {
            plainPos += match[1].length + 1 // @display
            markupPos += match[0].length
        } else {
            plainPos += 1
            markupPos += 1
        }
    }

    const beforeMarkup = markupValue.slice(0, markupPos)
    const afterMarkup = markupValue.slice(markupPos)

    const insertMarkup = `${mentionMarkup}${textAfter}`
    const insertPlain = `${mentionPlain}${textAfter}`

    return {
        nextMarkup: beforeMarkup + insertMarkup + afterMarkup,
        nextPlain: beforePlain + insertPlain + afterPlain,
        nextCaret: caret + insertPlain.length,
    }
}

// convert react-mentions markup to plain (used for building output)
const toPlainText = (markupValue) =>
    markupValue.replace(/@\[(.+?)\]\((.+?)\)/g, '@$1')

// remove ALL mentions from react-mentions markup value
// also removes any extra whitespace after mention
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// remove ALL mentions from text:
// - react-mentions markup: @[Display](id)
// - plain text mentions from saved replies: @Display
function stripAllMentions(markup, knownDisplays = []) {
    let next = markup || ''

    // remove react-mentions markup mentions
    next = next.replace(/@\[(.+?)\]\((.+?)\)\s*/g, '')

    // remove plain-text mentions for known users inserted by saved replies
    const uniqueDisplays = [...new Set((knownDisplays || []).filter(Boolean).map(normalizeDisplay))]
    uniqueDisplays.forEach((display) => {
        const escaped = escapeRegExp(display)
        next = next.replace(new RegExp(`(^|\\s)@${escaped}(?=\\s|$)`, 'g'), '$1')
    })

    return next
        .replace(/\s{2,}/g, ' ')
        .trimStart()
}

// insert text at caret using the actual textarea
function insertViaTextarea(textareaEl, text) {
    if (!textareaEl) return
    textareaEl.focus()
    let ok = false
    try { ok = document.execCommand('insertText', false, text) } catch {}
    if (ok) return

    const { selectionStart, selectionEnd, value } = textareaEl
    const before = value.slice(0, selectionStart)
    const after = value.slice(selectionEnd)
    const next = before + text + after
    const caret = before.length + text.length
    textareaEl.value = next
    textareaEl.setSelectionRange(caret, caret)
    textareaEl.dispatchEvent(new Event('input', { bubbles: true }))
}
// add this helper near the top
const normalizeDisplay = (s) => (s || '').replace(/^@+/, '');

// Build ONE inline string with JSON tokens for mentions
// Example: 'Hi  [{"value":"123","text":"Alice","prefix":" @"}]  thanks!'
function buildInlineMessage(plainText, mentions) {
    const list = (mentions || [])
        .map(m => ({
            id: String(m.id),
            display: m.display,
            index: typeof m.plainTextIndex === 'number' ? m.plainTextIndex :
                typeof m.index === 'number' ? m.index : -1
        }))
        .filter(m => m.index >= 0 && m.display)
        .sort((a, b) => a.index - b.index)

    const parts = []
    let pos = 0
    for (const m of list) {
        if (m.index > pos) {
            parts.push(plainText.slice(pos, m.index))
        }
        const cleanDisplay = normalizeDisplay(m.display);
        const token = [{ value: m.id, text: cleanDisplay, prefix: ' @' }]
        parts.push(JSON.stringify(token))
        // advance by the visible length of "@display"
        pos = m.index + 1 + cleanDisplay.length
    }
    if (pos < plainText.length) {
        parts.push(plainText.slice(pos))
    }
    return parts.join('')
}
const ReplyForm = ({ comment }) => {
    const dispatch = useDispatch()

    // react-mentions controlled values
    const [markupValue, setMarkupValue] = useState('')
    const [plainValue, setPlainValue] = useState('')

    // mentions captured from react-mentions + any “synthetic” ones (saved replies)
    const [mentionsState, setMentionsState] = useState([])
    const extraMentionsRef = useRef([])

    // keep last good state to rollback if a 2nd mention slips in
    const prevMarkupRef = useRef('')
    const prevPlainRef = useRef('')
    const prevMentionsRef = useRef([])

    const [showSavedReplies, setShowSavedReplies] = useState(false)
    const [instructionsShow, setInstructionsShow] = useState(false)

    const textareaRef = useRef(null)

    const privateRef = useRef(null)
    const instructRef = useRef(null)
    const aiFeedbackRef = useRef(null)
    const instructWrRef = useRef(null)
    const feedbackRef = useRef(null)
    const fSuggestRef = useRef(null)
    const toneRef = useRef(null)

    const {
        author_name,
        author_id,
        can_reply_privately,
        page_id,
        reviewed,
        replyEdit,
        has_brand,
    } = comment.attributes
    const replySent = useSelector((state) => state.ui.replySent)

    // helper: do we already have any mention (selected or synthetic)?
    const hasAnyMention = () =>
        (mentionsState && mentionsState.length ? 1 : 0) +
        (extraMentionsRef.current && extraMentionsRef.current.length ? 1 : 0) > 0

    // preload edit content
    useEffect(() => {
        const initial = replyEdit?.attributes?.raw_message || ''
        setMarkupValue(initial)
        setPlainValue(toPlainText(initial))
        setMentionsState([])
        extraMentionsRef.current = []
        prevMarkupRef.current = initial
        prevPlainRef.current = toPlainText(initial)
        prevMentionsRef.current = []
    }, [replyEdit])

    // onChange(event, markup, plain, mentions)
    const handleChange = (e, newMarkup, newPlain, newMentions) => {
        const arr = Array.isArray(newMentions) ? newMentions : []

        // hard limit: if adding a second mention (editor + synthetic), rollback
        if (arr.length + (extraMentionsRef.current?.length || 0) > 1) {
            setMarkupValue(prevMarkupRef.current)
            setPlainValue(prevPlainRef.current)
            setMentionsState(prevMentionsRef.current)
            return
        }

        setMarkupValue(newMarkup)
        setPlainValue(newPlain)
        setMentionsState(arr)

        // save as last good state
        prevMarkupRef.current = newMarkup
        prevPlainRef.current = newPlain
        prevMentionsRef.current = arr
    }

    // store a synthetic mention at a given plain-text index
    const pushExtraMentionAt = (id, display, index) => {
        const key = `${id}@${index}`
        if (!extraMentionsRef.current.some(m => m._key === key)) {
            extraMentionsRef.current.push({ id: String(id), display, index, _key: key })
        }
    }

    // Saved Replies: optionally prepend a mention with ID at caret, then text
    const setMessageWithMention = (msg, mention) => {
        const ta = textareaRef.current
        const shouldInsertMention = mention && privateAllowed && !hasAnyMention()

        if (shouldInsertMention) {
            const { nextMarkup, nextPlain, nextCaret } = insertMentionMarkupAtCaret({
                markupValue,
                plainValue,
                textareaEl: ta,
                mentionId: author_id,
                mentionDisplay: author_name,
                textAfter: ` ${msg || ''}`,
            })

            setMarkupValue(nextMarkup)
            setPlainValue(nextPlain)
            setMentionsState([{ id: String(author_id), display: author_name }])

            prevMarkupRef.current = nextMarkup
            prevPlainRef.current = nextPlain
            prevMentionsRef.current = [{ id: String(author_id), display: author_name }]

            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus()
                    try {
                        textareaRef.current.setSelectionRange(nextCaret, nextCaret)
                    } catch {}
                }
            })

            return
        }

        // no mention case: just insert plain text
        if (!ta) {
            setMarkupValue(v => (v || '') + (msg || ''))
            setPlainValue(v => (v || '') + (msg || ''))

            prevMarkupRef.current = (markupValue || '') + (msg || '')
            prevPlainRef.current = (plainValue || '') + (msg || '')
            return
        }

        insertViaTextarea(ta, msg || '')
    }

    // Emoji insert
    const handleEmojiSelect = (emoji) => {
        const text = emoji?.native || emoji
        insertViaTextarea(textareaRef.current, text)
    }

    // private checkbox toggles suggestions; also compute allowMentions from current state
    const [privateAllowed, setPrivateAllowed] = useState(true)
    const allowMentions = privateAllowed && !hasAnyMention()
    const handlePrivate = (e) => {
        const isPrivate = e.target.checked

        if (isPrivate) {
            const nextMarkup = stripAllMentions(markupValue, [author_name])
            const nextPlain = toPlainText(nextMarkup)

            setMarkupValue(nextMarkup)
            setPlainValue(nextPlain)

            setMentionsState([])
            extraMentionsRef.current = []

            prevMarkupRef.current = nextMarkup
            prevPlainRef.current = nextPlain
            prevMentionsRef.current = []
        }

        setPrivateAllowed(!isPrivate)
    }

    const handleSubmit = (e, archive = false) => {
        e.preventDefault()
        if (!replySent.includes(comment.id) || replyEdit) {
            if (!replyEdit) dispatch(setReplySent(comment.id))

            const plain = (plainValue || '')
            const trimmed = plain.trim()
            if (trimmed) {
                // combine mentions from editor + synthetic, keep earliest only
                const combined = [
                    ...mentionsState.map(m => ({
                        id: String(m.id),
                        display: normalizeDisplay(m.display),
                        index: typeof m.plainTextIndex === 'number' ? m.plainTextIndex :
                            typeof m.index === 'number' ? m.index : -1
                    })),
                    ...extraMentionsRef.current.map(m => ({
                        id: String(m.id),
                        display: normalizeDisplay(m.display),
                        index: m.index
                    }))
                ]
                    .filter(m => m.index >= 0 && m.display)
                    .sort((a,b) => a.index - b.index)
                    .slice(0, 1) // enforce only ONE mention in payload

                // Build inline string with embedded JSON token(s)
                const inlineMessage = buildInlineMessage(plain, combined)

                const formData = new FormData()
                formData.append('comment[message]', inlineMessage)
                if (privateRef.current) formData.append('private', String(privateRef.current.checked))

                if (replyEdit) {
                    dispatch(editReply(comment.id, formData, page_id, replyEdit.id))
                    dispatch(updateComment(comment.id, { replyEdit: null }))
                } else {
                    dispatch(sendReply(comment.id, formData, page_id))
                }

                if (archive) {
                    if (!reviewed) dispatch(addNewTmpComment(comment.id))
                    dispatch(setCommentReview(comment.id, reviewed, page_id))
                }
            } else {
                alert('Please fill in the reply field.')
            }
        }
    }

    const closeForm = () => {
        dispatch(updateComment(comment.id, { replyEdit: null }))
        dispatch(updateComment(comment.id, { show_reply_form: false }))
    }

    // feedback popover
    useEffect(() => {
        const clickOutsideFeedback = (e) => {
            const parent = feedbackRef.current
            if (fSuggestRef.current && parent && !parent.contains(e.target)) {
                fSuggestRef.current.style.display = 'none'
            }
        }
        document.addEventListener('click', clickOutsideFeedback)
        return () => document.removeEventListener('click', clickOutsideFeedback)
    }, [])

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const usersData = [{ id: String(author_id), display: author_name }]

    return (
        <>
            <div className="col-12 mt-2 reply-collapse no-transition collapse show">
                <div className="reply-block mb-3">
                    <div className="w-100">
                        <div className="text-group w-100 mb-2">
              <span className="close-sm">
                <Button type="reset" className="btn btn-tr p-0" clickHandler={closeForm}>
                  <FontAwesomeIcon icon={faXmark} className="saved-replies-icon mx-2" size="lg" />
                </Button>
              </span>
                            <div className="tags-input">
                                {/* Mentions input: ONLY @mentions */}
                                <MentionsInput
                                    value={markupValue}
                                    onChange={handleChange}
                                    placeholder="Type your comment here…"
                                    inputRef={(el) => { textareaRef.current = el }}
                                    style={mentionsStyle}
                                >
                                    <Mention
                                        trigger="@"
                                        data={allowMentions ? usersData : []}
                                        appendSpaceOnAdd
                                        markup="@[__display__](__id__)"
                                        displayTransform={(id, display) => `@${display}`}
                                        style={suggestionsListStyle}
                                        renderSuggestion={(entry, search, highlighted, index, focused) => (
                                            <div
                                                key={entry.id}
                                                style={{ ...suggestionItemBase, background: focused ? '#f3f4f6' : '#fff' }}
                                            >
                                                {highlighted}
                                            </div>
                                        )}
                                    />
                                </MentionsInput>
                            </div>
                        </div>

                        <div className="d-flex justify-content-between mx-0 reply-bottom">
                            <div className="d-flex reply-p p-0">
                                {can_reply_privately && (replyEdit ? replyEdit?.attributes?.can_edit : true) && (
                                    <div className="private-checkbox">
                                        <input
                                            className="form-check-input mt-0"
                                            type="checkbox"
                                            onChange={handlePrivate}
                                            ref={privateRef}
                                        />
                                        <label className="form-check-label ms-2">Send as private message</label>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex reply-b p-0 icon-buttons position-relative">
                                <FontAwesomeIcon
                                    icon={faPaste}
                                    className="saved-replies-icon mx-2"
                                    size="lg"
                                    onClick={() => setShowSavedReplies(true)}
                                />

                                <Translate
                                    pageId={page_id}
                                    objectId={comment.id}
                                    isMobile={true}
                                    className="btn btn-tr saved-replies-icon p-0 mx-2"
                                    inputRef={{
                                        current: {
                                            value: plainValue,
                                            focus: () => textareaRef.current?.focus(),
                                        },
                                    }}
                                />

                                <FontAwesomeIcon
                                    icon={faFaceSmile}
                                    className="emoji-icon mx-2"
                                    size="lg"
                                    onClick={() => dispatch(setShowEmojiModal(null, 'comment_message'))}
                                />

                                <EmojiContainer className="emoji-wrapper" handleEmojiSelect={handleEmojiSelect} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between mb-2">
                    <div className="d-flex">
                        <AiAction
                            objectId={comment.id}
                            pageId={page_id}
                            msgRef={{ current: { value: plainValue, focus: () => textareaRef.current?.focus() } }}
                            instructRef={instructRef}
                            toneRef={toneRef}
                            feedbackRef={feedbackRef}
                            instructWrRef={instructWrRef}
                            setMessage={(text, del = false) => {
                                const ta = textareaRef.current;

                                if (del) {
                                    if (ta) {
                                        ta.value = '';
                                        ta.dispatchEvent(new Event('input', { bubbles: true }));
                                    } else {
                                        setMarkupValue('');
                                        setPlainValue('');
                                    }
                                    // keep your existing clears
                                    extraMentionsRef.current = [];
                                    prevMarkupRef.current = '';
                                    prevPlainRef.current = '';
                                    prevMentionsRef.current = [];
                                    // <-- DO NOT return here
                                }

                                if (text && ta) {
                                    // ensure caret is at end before inserting
                                    try {
                                        ta.focus();
                                        ta.setSelectionRange(ta.value.length, ta.value.length);
                                    } catch {}
                                    insertViaTextarea(ta, text);
                                } else if (text && !ta) {
                                    // fallback if ref not ready
                                    setMarkupValue(prev => (prev || '') + text);
                                    setPlainValue(prev => (prev || '') + text);
                                }
                            }}
                            aiFeedbackRef={aiFeedbackRef}
                            hasBrand={has_brand}
                        />
                    </div>
                    <div className="d-flex reply-p p-0">
                        <Button type="reset" className="btn btn-tr close-lg" clickHandler={closeForm}>
                            Cancel
                        </Button>
                        <Button type="submit" className="btn btn-secondary mx-2" clickHandler={(e) => handleSubmit(e, true)}>
                            Reply and archive
                        </Button>
                        <Button type="submit" className="btn btn-primary" clickHandler={handleSubmit}>
                            Reply
                        </Button>
                    </div>
                </div>

                <div className="add-instructions" ref={instructWrRef}>
                    {instructionsShow ? (
                        <>
                            <div className="instruct-link" onClick={() => setInstructionsShow(false)}>
                                Remove additional instructions
                            </div>
                            <div className="col-lg-4 col-md-4 col-xs-12 mt-2 form-group mb-2">
                                <select className="form-select" name="comment[tone_of_voice]" ref={toneRef} defaultValue="">
                                    <option value="" disabled>Choose a tone of voice</option>
                                    <option value="professional">Professional</option>
                                    <option value="funny">Funny</option>
                                    <option value="conversational">Conversational</option>
                                    <option value="casual">Casual</option>
                                    <option value="helpful">Helpful</option>
                                </select>
                            </div>
                            <textarea
                                className="form-control"
                                placeholder="Mention the 30% off sale we're currently running"
                                rows={2}
                                ref={instructRef}
                                name="comment[instructions]"
                            />
                        </>
                    ) : (
                        <div className="instruct-link" onClick={() => setInstructionsShow(true)}>
                            Add additional instructions
                        </div>
                    )}
                </div>

                <div className="ai-feedback my-2" ref={aiFeedbackRef}>
          <textarea
              className="form-control f-inp"
              placeholder="Ask AI to improve its response"
              rows={1}
              ref={feedbackRef}
              onClick={() => {
                  if (feedbackRef.current && feedbackRef.current.value === '' && fSuggestRef.current) {
                      fSuggestRef.current.style.display = 'block'
                  }
              }}
              onChange={(e) => {
                  if (fSuggestRef.current) {
                      fSuggestRef.current.style.display = e.target.value === '' ? 'block' : 'none'
                  }
              }}
              name="comment[feedback_instructions]"
          />
                    <div className="f-suggest" ref={fSuggestRef}>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Make it longer
                        </div>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Make it shorter
                        </div>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Be more enthusiastic
                        </div>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Be more neutral
                        </div>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Use simpler language
                        </div>
                        <div className="f-suggest-item" onClick={(e) => (feedbackRef.current.value = e.target.innerText)}>
                            Include a call to action
                        </div>
                    </div>
                </div>
            </div>

            {showSavedReplies && (
                <SavedRepliesModal
                    setShowSavedReplies={setShowSavedReplies}
                    pageId={page_id}
                    setMessage={setMessageWithMention}
                />
            )}

            <ReactTooltip effect="solid" html={true} className="switch-tooltip" />
        </>
    )
}

export default React.memo(ReplyForm)
